import { GeneralInputProps } from '@/components/GeneralInputs/type';
import {
  FormInput,
  FormInputProps,
} from '@moneyforward/ap-frontend-components';
import { memo, useCallback, useMemo } from 'react';
import { FieldValues, useController } from 'react-hook-form';

export type AlphanumericTypeProps<TFieldValues extends FieldValues> =
  FormInputProps<TFieldValues>;

type AlphanumericTypeInputProps<TFieldValues extends FieldValues> =
  AlphanumericTypeProps<TFieldValues> & GeneralInputProps;

const InnerAlphanumericTypeInput = <TFieldValues extends FieldValues>({
  control,
  inputId,
  rules,
  inputProps: _inputProps,
  name,
  onChangeAfter,
  placeholder,
}: AlphanumericTypeInputProps<TFieldValues>) => {
  const {
    field: { value, onChange: fieldOnChange },
  } = useController({
    name: name,
    control,
    defaultValue: undefined,
  });
  const inputProps = useMemo(() => {
    return {
      ..._inputProps,
      placeholder: placeholder,
    };
  }, [placeholder, _inputProps]);

  const handleConvertFullWidthToHalfWidth = useCallback(() => {
    const currentValue = String(value || '');
    if (!currentValue) return;
    const convertedStr = currentValue
      .replace(/[０-９Ａ-Ｚａ-ｚ]/g, (match) => {
        return String.fromCharCode(match.charCodeAt(0) - 0xfee0);
      })
      .replace(/\u3000/g, ' ');
    fieldOnChange(convertedStr);
  }, [value, fieldOnChange]);

  return (
    <FormInput<TFieldValues>
      control={control}
      inputId={inputId}
      rules={rules}
      inputProps={inputProps}
      name={name}
      onChangeAfter={onChangeAfter}
      onBlur={handleConvertFullWidthToHalfWidth}
    />
  );
};
InnerAlphanumericTypeInput.displayName = 'InnerAlphanumericTypeInput';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const AlphanumericTypeInput = memo(
  InnerAlphanumericTypeInput
) as typeof InnerAlphanumericTypeInput;
AlphanumericTypeInput.displayName = 'AlphanumericTypeInput';
